// src/composables/usePush.js
import { ref, readonly } from 'vue';
import { Capacitor } from '@capacitor/core';
import UnifiedPush from '@/plugins/UnifiedPush';
import { useUserStore } from '@/stores/userStore';

const token = ref('');
const platform = ref('unknown');
const isReady = ref(false);
const permissionState = ref('unknown'); // 'granted' | 'denied' | 'prompt' | 'unknown'

let listenersBound = false;

export function usePush() {
  const userStore = useUserStore();

  /**
   * Главная функция — вызывается при старте приложения и после логина.
   * Автоматически определяет, нужно ли запрашивать разрешение.
   */
  async function initPush() {
    const isNative = Capacitor.isNativePlatform();
    if (!isNative) {
      console.log('[push] web platform — native push skipped');
      return false;
    }

    try {
      // 1️⃣ Проверяем текущее состояние разрешения
      const perm = await UnifiedPush.checkPermissions();
      permissionState.value = perm.state;
      console.log('[push] текущее состояние разрешения:', perm.state);

      // 2️⃣ Если разрешение ещё не запрашивали (prompt) — запрашиваем
      if (perm.state === 'prompt') {
        const result = await UnifiedPush.requestPermissions();
        permissionState.value = result.state;
        
        if (result.state !== 'granted') {
          console.warn('[push] пользователь отклонил разрешение');
          // Здесь можно показать свой UI с объяснением
          return false;
        }
      }

      // 3️⃣ Если разрешение отклонено — НЕ запрашиваем снова через системный диалог
      // (он всё равно не появится). Вместо этого — показываем кнопку "Включить в настройках"
      if (perm.state === 'denied') {
        console.warn('[push] разрешение отклонено, нужен переход в настройки');
        // Можно установить флаг, чтобы UI показал кнопку
        return false;
      }

      // 4️⃣ Разрешение есть — получаем токен
      const { platform: p } = await UnifiedPush.getPlatform();
      platform.value = p;

      if (p === 'unknown') {
        console.warn('[push] платформа неизвестна');
        return false;
      }

      const res = await UnifiedPush.getToken();
      token.value = res.token;

      // 5️⃣ Регистрируем на бэкенде
      await userStore.registerDevice({
        token: res.token,
        platform: p,
      });

      // 6️⃣ Подписки — только один раз
      if (!listenersBound) {
        await bindListeners();
        listenersBound = true;
      }

      isReady.value = true;
      console.log('[push] инициализирован:', p);
      return true;
    } catch (error) {
      console.error('[push] init failed:', error);
      return false;
    }
  }

  /**
   * Повторно запросить разрешение (например, по кнопке в UI).
   * ВАЖНО: если пользователь уже отказал — системный диалог НЕ появится.
   * В этом случае нужно открывать настройки приложения.
   */
  async function requestPermissionAgain() {
    const isNative = Capacitor.isNativePlatform();
    if (!isNative) return false;

    const result = await UnifiedPush.requestPermissions();
    permissionState.value = result.state;

    if (result.state === 'granted') {
      // Разрешение получено — инициализируем push
      await initPush();
      return true;
    }

    // Если снова denied — значит, пользователь ранее поставил "Больше не спрашивать"
    // В этом случае нужно открыть настройки приложения
    if (result.state === 'denied') {
      await openAppSettings();
    }

    return false;
  }

  /**
   * Открыть настройки приложения (чтобы пользователь вручную включил уведомления)
   */
  async function openAppSettings() {
    const isNative = Capacitor.isNativePlatform();
    if (!isNative) return;

    try {
      // Для этого нужен отдельный метод в нативном плагине (см. ниже)
      await UnifiedPush.openSettings();
    } catch (error) {
      console.error('[push] не удалось открыть настройки:', error);
    }
  }

  async function bindListeners() {
    await UnifiedPush.addListener('tokenRefreshed', async ({ token: newToken, platform: p }) => {
      token.value = newToken;
      platform.value = p;
      await userStore.registerDevice({ token: newToken, platform: p });
    });

    await UnifiedPush.addListener('pushReceived', (data) => {
      console.log('[push] получено:', data);
    });
  }

  return {
    token: readonly(token),
    platform: readonly(platform),
    isReady: readonly(isReady),
    permissionState: readonly(permissionState),
    initPush,
    requestPermissionAgain,
    openAppSettings,
  };
}